import React, { useState } from "react";
import { Download } from "lucide-react";
import useAsync from "../../utils/useAsync.js";
import { getStatement } from "../../utils/stockApi.js";
import { fmtCell, downloadCsv } from "../../utils/format.js";
import { Loading, ErrorBox, SubTabs } from "./common.jsx";
import BarChart from "./BarChart.jsx";
import QuarterlyTable from "./QuarterlyTable.jsx";

const STATEMENT_TABS = [
  ["income", "Income Statement"],
  ["balance", "Balance Sheet"],
  ["cashflow", "Cash Flow"],
  ["ratios", "Ratios"],
];
const PERIODS = [["annual", "Annual"], ["quarterly", "Quarterly"], ["ltm", "LTM"]];
const UNITS = ["K", "MM", "B"];

export default function FinancialsTab({ ticker }) {
  const [statement, setStatement] = useState("income");
  const [period, setPeriod] = useState("annual");
  const [unit, setUnit] = useState("MM");
  const [selected, setSelected] = useState(null);

  const { data, loading, error } = useAsync(
    () => getStatement(ticker, statement, period),
    [ticker, statement, period]
  );

  const chartRow = data && (data.rows.find((r) => r.key === selected) || null);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(`${ticker}-${statement}-${period}.csv`, [
      ["($MM unless noted)", ...data.columns.map((c) => c.label)],
      ...data.rows.map((r) => [r.label, ...r.values.map((v) => (v === null ? "" : v))]),
    ]);
  };

  return (
    <div>
      <div className="co-toolbar">
        <SubTabs items={STATEMENT_TABS} value={statement} onChange={(s) => { setStatement(s); setSelected(null); }} />
        <div className="co-toolbar-right">
          <div className="co-seg">
            {PERIODS.map(([k, l]) => (
              <button key={k} className={period === k ? "active" : ""} onClick={() => setPeriod(k)}>{l}</button>
            ))}
          </div>
          {statement !== "ratios" && (
            <div className="co-seg" title="Display units">
              {UNITS.map((u) => (
                <button key={u} className={unit === u ? "active" : ""} onClick={() => setUnit(u)}>{u}</button>
              ))}
            </div>
          )}
          <button className="btn tool-btn" onClick={exportCsv} disabled={!data}>
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {statement === "income" && period === "quarterly" ? <div className="card co-card"><QuarterlyTable ticker={ticker} count={24} unit={unit} /></div> : loading && !data ? <Loading /> : error ? <ErrorBox error={error} /> : (
        <>
          {chartRow && (
            <div className="card co-card">
              <h6 className="co-card-title">
                {chartRow.label}
                <button className="co-link" onClick={() => setSelected(null)}>close</button>
              </h6>
              <BarChart
                labels={data.columns.map((c) => c.label)}
                values={chartRow.values}
                format={(v) => fmtCell(v, chartRow.kind, unit)}
              />
            </div>
          )}

          <div className="card co-card co-table-wrap">
            <table className="co-table">
              <thead>
                <tr>
                  <th className="co-sticky">
                    {statement === "ratios" ? "Ratios" : `USD in ${unit === "K" ? "thousands" : unit === "MM" ? "millions" : "billions"}`}
                  </th>
                  {data.columns.map((c) => (
                    <th key={c.label}>
                      {c.label}
                      <small>{c.sub}</small>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr
                    key={r.key}
                    className={`${r.bold ? "bold" : ""} ${r.italic ? "italic" : ""} ${selected === r.key ? "selected" : ""}`}
                    onClick={() => setSelected(r.key === selected ? null : r.key)}
                    title="Click to chart this row"
                  >
                    <td className={`co-sticky ${r.indent ? "indent" : ""}`}>{r.label}</td>
                    {r.values.map((v, i) => (
                      <td key={i} className={v < 0 && r.kind !== "money" ? "neg" : ""}>
                        {fmtCell(v, r.kind, unit)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="co-hint">Click any row to see it as a chart.</p>
        </>
      )}
    </div>
  );
}
